import * as THREE from 'three';
import { StaticClusters, type StaticUpload } from './static-clusters';

interface Page {
  mesh: THREE.BatchedMesh;
  material: number;
  instances: number;
  vertices: number;
  capacity: number;
}
interface Slot {
  page: Page;
  geometry: number;
  instance: number;
  vertices: number;
}

/** Stable owner IDs map to material-specific multi-draw slots. A deletion frees
 * geometry/instance capacity; empty pages release their GPU buffers and textures.
 * Authoritative meshes remain available for reconstruction and targeted edits.
 */
export class ClusterBatches {
  readonly root = new THREE.Group();
  private pages = new Set<Page>();
  private owners = new Map<number, Slot[]>();
  private fixed: StaticClusters;
  private sections = new Map<number, Map<string, { id: number; geometry: THREE.BufferGeometry }>>();
  private nextSection = -1;
  constructor(private material: THREE.Material | THREE.Material[]) {
    this.root.name = 'Structural material batches';
    this.fixed = new StaticClusters(Array.isArray(material) ? material : [material]);
    this.root.add(this.fixed.root);
  }
  prepareFixed(geometry: THREE.BufferGeometry, matrix: THREE.Matrix4): Generator<void, StaticUpload> {
    return this.fixed.prepare(geometry, matrix);
  }
  set(
    id: number,
    geometry: THREE.BufferGeometry,
    matrix: THREE.Matrix4,
    fixed = false,
    upload?: StaticUpload,
  ): void {
    this.remove(id);
    if (fixed) {
      this.fixed.set(id, geometry, matrix, upload);
      return;
    }
    const slots: Slot[] = [];
    for (const group of geometry.groups) {
      const count = group.count;
      if (!count) continue;
      const material = group.materialIndex ?? 0;
      let page: Page | undefined;
      for (const candidate of this.pages) {
        if (
          candidate.material !== material ||
          candidate.instances >= 1024 ||
          candidate.capacity - candidate.vertices < count
        )
          continue;
        if (candidate.mesh.unusedVertexCount < count) candidate.mesh.optimize();
        page = candidate;
        break;
      }
      if (!page) {
        const capacity = Math.max(65536, 2 ** Math.ceil(Math.log2(count)));
        const materials = Array.isArray(this.material) ? this.material : [this.material];
        const mesh = new THREE.BatchedMesh(1024, capacity, 0, materials[material] ?? materials[0]);
        mesh.frustumCulled = false;
        mesh.sortObjects = false;
        mesh.castShadow = mesh.receiveShadow = true;
        page = { mesh, material, instances: 0, vertices: 0, capacity };
        this.pages.add(page);
        this.root.add(mesh);
      }
      const part = new THREE.BufferGeometry();
      for (const name of ['position', 'normal', 'color']) {
        const attribute = geometry.getAttribute(name) as THREE.BufferAttribute;
        const data = attribute.array.subarray(
          group.start * attribute.itemSize,
          (group.start + count) * attribute.itemSize,
        );
        part.setAttribute(name, new THREE.BufferAttribute(data, attribute.itemSize));
      }
      const geometryId = page.mesh.addGeometry(part);
      const instance = page.mesh.addInstance(geometryId);
      page.mesh.setMatrixAt(instance, matrix);
      page.instances++;
      page.vertices += count;
      slots.push({ page, geometry: geometryId, instance, vertices: count });
      part.dispose();
    }
    this.owners.set(id, slots);
  }
  setSections(id: number, parts: Map<string, THREE.BufferGeometry>, matrix: THREE.Matrix4): void {
    let existing = this.sections.get(id);
    if (!existing) {
      this.remove(id);
      this.sections.set(id, (existing = new Map()));
    }
    for (const [key, part] of existing)
      if (!parts.has(key)) {
        this.remove(part.id);
        existing.delete(key);
      }
    for (const [key, geometry] of parts) {
      const prior = existing.get(key);
      if (prior?.geometry === geometry) continue;
      const partId = prior?.id ?? this.nextSection--;
      this.set(partId, geometry, matrix, true);
      existing.set(key, { id: partId, geometry });
    }
  }
  transform(id: number, matrix: THREE.Matrix4): void {
    for (const slot of this.owners.get(id) ?? []) slot.page.mesh.setMatrixAt(slot.instance, matrix);
  }
  remove(id: number): void {
    for (const part of this.sections.get(id)?.values() ?? []) this.remove(part.id);
    this.sections.delete(id);
    this.fixed.remove(id);
    for (const slot of this.owners.get(id) ?? []) {
      const page = slot.page;
      page.mesh.deleteGeometry(slot.geometry);
      page.instances--;
      page.vertices -= slot.vertices;
      if (!page.instances) {
        this.root.remove(page.mesh);
        page.mesh.dispose();
        this.pages.delete(page);
      }
    }
    this.owners.delete(id);
  }
  clear(): void {
    for (const step of this.clearSteps()) void step;
  }
  *clearSteps(): Generator<void> {
    this.sections.clear();
    this.nextSection = -1;
    yield* this.fixed.clearSteps();
    for (const page of this.pages) {
      this.root.remove(page.mesh);
      page.mesh.dispose();
      yield;
    }
    this.pages.clear();
    this.owners.clear();
    this.root.clear();
    this.root.add(this.fixed.root);
  }
  get stats(): Record<string, number> {
    let vertices = 0,
      capacity = 0;
    for (const p of this.pages) {
      vertices += p.vertices;
      capacity += p.capacity;
    }
    const fixed = this.fixed.stats;
    return {
      structuralBatchPages: this.pages.size + fixed.pages,
      batchedOwners: this.owners.size + fixed.owners,
      batchVertices: vertices + fixed.vertices,
      batchCapacity: capacity + fixed.capacity,
      movingBatchPages: this.pages.size,
      staticBatchPages: fixed.pages,
      staticPageGrows: fixed.pageGrows,
      staticGrowthCopyVertices: fixed.growthCopyVertices,
    };
  }
}
